import { ColorContext, LayoutSizeContext, SidebarContext } from 'App';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import { FaLocationCrosshairs } from 'react-icons/fa6';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import LocationSetter from './AlertCreator/components/LocationSetter';
import MapReset from './BaseMap/components/MapReset';
import MapView from './BaseMap/components/MapView';
import ResetZoomMapEvent from './ResetZoomMapEvent';
import ZoomOnFocusMapEvent from './ZoomOnFocusMapEvent';

const parseCenter = (value) => {
  const MAX_LAT = 85.05112878; // Clamp to Web Mercator limits to avoid infinite tile requests

  const clampLatLng = (lat, lng) => [
    Math.min(Math.max(lat, -MAX_LAT), MAX_LAT),
    Math.min(Math.max(lng, -180), 180),
  ];

  if (!value) return null;
  if (Array.isArray(value)) {
    const lat = Number.parseFloat(value[0]);
    const lng = Number.parseFloat(value[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? clampLatLng(lat, lng) : null;
  }
  if (typeof value === 'object') {
    const lat = Number.parseFloat(value.lat ?? value.latitude);
    const lng = Number.parseFloat(value.lng ?? value.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? clampLatLng(lat, lng) : null;
  }
  return null;
};

export default function FullScreenMap({
  mapRef,
  children,
  mapCenter,
  zoom,
  maxBounds,
  maxBoundsViscosity,
  minZoom = 0,
  maxZoom = 19,
  setView,
  showMapSat = true,
  classNameStyle,
  cssStyle,
  isDopplerMap = false,
}) {
  const sidebarOpen = useContext(SidebarContext);
  const layout = useContext(LayoutSizeContext);
  const theme = useContext(ColorContext);

  const parsedCenter = useMemo(() => parseCenter(mapCenter) || [0, 0], [mapCenter]);

  const targetZoom = Number.isFinite(zoom) ? zoom : 5;

  const [satelliteView, setSatelliteView] = useState(false);
  const [labelsEnabled, setLabelsEnabled] = useState(true);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [initPos, setInitPos] = useState(parsedCenter);
  const [initZoom, setInitZoom] = useState(targetZoom);
  const [resetTrigger, setResetTrigger] = useState(false); // State to trigger reset
  const [interacted, setInteracted] = useState(false); // State to track user interaction
  const containerRef = useRef(null);

  useEffect(() => {
    setInitPos(parsedCenter);
  }, [parsedCenter]);

  useEffect(() => {
    setInitZoom(targetZoom);
  }, [targetZoom]);

  const handleResetClick = () => {
    setInteracted(false); // Reset interacted state
    setResetTrigger(new Date()); // Toggle to trigger useEffect in ResetZoomMapEvent
  };

  useEffect(() => {
    const target = containerRef.current;
    if (!target) return undefined;

    let frame = 0;
    const notifyResize = () => {
      if (!mapRef?.current || !mapRef.current.invalidateSize) return;
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        mapRef.current.invalidateSize({ animate: false });
      });
    };

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', notifyResize);
      notifyResize();
      return () => {
        window.removeEventListener('resize', notifyResize);
        if (frame) cancelAnimationFrame(frame);
      };
    }

    const observer = new ResizeObserver(() => notifyResize());
    observer.observe(target);
    notifyResize();
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [mapRef]);

  const InvalidateSize = ({ layout, sidebarOpen }) => {
    const map = useMap();
    useEffect(() => {
      if (map != null) {
        map.invalidateSize();
      }
    }, [map, layout.width, sidebarOpen]);
    return null;
  };

  const isXxl = layout.width >= 1400;
  const isLgUp = layout.width >= 992;

  const resetButtonStyle = useMemo(() => {
    if (!isLgUp) {
      return {
        bottom: 80,
        left: 20,
        width: 30,
        height: 30,
      };
    }

    const top = layout.width >= 1600 ? 120 : layout.width >= 1200 ? 100 : 90;

    return {
      top,
      left: 20,
      width: 30,
      height: 30,
    };
  }, [isLgUp, layout.width]);

  return (
    <div ref={containerRef} className="position-relative flex-fill w-100 h-100 bg-white">
      <MapContainer
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        ref={mapRef}
        center={parsedCenter}
        zoom={targetZoom}
        maxBounds={maxBounds}
        maxBoundsViscosity={maxBoundsViscosity}
        minZoom={minZoom}
        maxZoom={maxZoom}
        setView={setView}
        scrollWheelZoom={zoomEnabled}
        touchZoom={zoomEnabled}
        zIndex={0}
      >
        {satelliteView ? (
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            maxNativeZoom={19}
            maxZoom={maxZoom}
          />
        ) : (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            maxNativeZoom={19}
            maxZoom={maxZoom}
          />
        )}
        {labelsEnabled && satelliteView && (
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            maxNativeZoom={19}
            maxZoom={maxZoom}
          />
        )}
        {children}
        <ZoomOnFocusMapEvent setInteracted={setInteracted} />
        <ResetZoomMapEvent
          triggerReset={resetTrigger}
          initPos={initPos}
          initZoom={initZoom}
          setInitPos={setInitPos}
          setInitZoom={setInitZoom}
        />
        <InvalidateSize layout={layout} sidebarOpen={sidebarOpen} />
        {mapCenter && <LocationSetter center={parsedCenter} zoom={targetZoom} />}

        {interacted && <MapReset onClick={handleResetClick} interacted={interacted} />}
        {/* Only show MapView if not Doppler map */}
        {!isDopplerMap && (
          <MapView
            satelliteView={satelliteView}
            labelsEnabled={labelsEnabled}
            onToggleSatellite={() => setSatelliteView((prev) => !prev)}
            onToggleLabels={() => setLabelsEnabled((prev) => !prev)}
          />
        )}
      </MapContainer>
      {isDopplerMap && (
        <>
          {/* Only show old switch UI for Doppler maps */}
          {!showMapSat && (
            <Stack direction="horizontal" gap={2} className={classNameStyle} style={cssStyle}>
              <Form.Check
                type="switch"
                id="map-satellite-toggle"
                label={<span className="text-white">{satelliteView ? 'Satellite' : 'Map'}</span>}
                className="text-white"
                style={{ accentColor: theme.primary }}
                checked={satelliteView}
                onChange={() => setSatelliteView(!satelliteView)}
              />
              {satelliteView && (
                <Form.Check
                  id="map-label-toggle"
                  type="checkbox"
                  className="text-white"
                  style={{ accentColor: theme.primary }}
                  label={<span className="text-white">Labels</span>}
                  checked={labelsEnabled}
                  onChange={() => setLabelsEnabled(!labelsEnabled)}
                />
              )}
            </Stack>
          )}

          {/* Only show bottom switch UI for non-Doppler maps */}
          {showMapSat && (
            <Stack
              direction="horizontal"
              gap={2}
              className="position-absolute bottom-0 start-0 z-2 ms-1 mb-1"
            >
              <Form.Check
                type="switch"
                id="map-satellite-toggle-bottom"
                label={<span className="text-white">{satelliteView ? 'Satellite' : 'Map'}</span>}
                className="text-white"
                style={{ accentColor: theme.primary }}
                checked={satelliteView}
                onChange={() => setSatelliteView(!satelliteView)}
              />
              {satelliteView && (
                <Form.Check
                  id="map-label-toggle-bottom"
                  type="checkbox"
                  className="text-white"
                  style={{ accentColor: theme.primary }}
                  label={<span className="text-white text-outline">Labels</span>}
                  checked={labelsEnabled}
                  onChange={() => setLabelsEnabled(!labelsEnabled)}
                />
              )}
            </Stack>
          )}
        </>
      )}
    </div>
  );
}
